import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, AlertTriangle } from 'lucide-react';
import { ConfirmationModal } from '../../components/admin/modals';
import { api } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

export default function AdminSettings() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingMaintenance, setPendingMaintenance] = useState<boolean | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { data: maintenanceData, isLoading } = useQuery({
    queryKey: ['admin-maintenance-mode'],
    queryFn: () => api.getAdminMaintenanceMode(),
    refetchInterval: 10000,
  });

  const maintenanceMutation = useMutation({
    mutationFn: (enabled: boolean) => api.setAdminMaintenanceMode(enabled),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-maintenance-mode'] });
      setSuccessMessage(data.enabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  useEffect(() => {
    if (maintenanceData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset pending state when server data updates
      setPendingMaintenance(null);
    }
  }, [maintenanceData]);

  const handleToggleMaintenance = () => {
    const newValue = !maintenanceData?.enabled;
    setPendingMaintenance(newValue);
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    if (pendingMaintenance !== null) {
      maintenanceMutation.mutate(pendingMaintenance);
    }
    setShowConfirm(false);
    setPendingMaintenance(null);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setPendingMaintenance(null);
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {/* Maintenance Mode Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gray-100">
            <Settings className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{t('admin.settings.maintenanceMode', 'Maintenance Mode')}</h3>
            <p className="text-sm text-gray-600 mt-1">
              When enabled, non-admin users will see a maintenance page instead of the normal site.
            </p>

            <div className="mt-4 flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                maintenanceData?.enabled
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  maintenanceData?.enabled ? 'bg-amber-500' : 'bg-green-500'
                }`} />
                <span className="text-sm font-medium">
                  {isLoading ? 'Loading...' : maintenanceData?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              <button
                onClick={handleToggleMaintenance}
                disabled={isLoading || maintenanceMutation.isPending}
                className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  maintenanceData?.enabled
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                }`}
              >
                {maintenanceMutation.isPending
                  ? 'Updating...'
                  : maintenanceData?.enabled
                  ? 'Disable Maintenance'
                  : 'Enable Maintenance'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Important</p>
          <p className="text-sm text-amber-700 mt-1">
            Enabling maintenance mode will immediately prevent all non-admin users from accessing the site.
            Admin users can still access all features while maintenance mode is active.
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirm}
        title={pendingMaintenance ? 'Enable Maintenance Mode?' : 'Disable Maintenance Mode?'}
        message={
          pendingMaintenance
            ? 'All non-admin users will be shown the maintenance page. Are you sure you want to continue?'
            : 'The site will be accessible to all users again. Are you sure you want to continue?'
        }
        confirmLabel={pendingMaintenance ? 'Enable' : 'Disable'}
        variant={pendingMaintenance ? 'warning' : 'info'}
        isLoading={maintenanceMutation.isPending}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
